import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { Component } from 'react';
import { connect } from 'react-redux';
import { CanonicalLoader, LandscapeState } from '@wayfinder/vms-state';
import { MapNote } from '@wayfinder/vms-base';
import s from './Venue.module.scss';
import * as VmsState from '../../backend/index-vms-state';
import { ConfirmationModal, SkeletonLoader, TopBar, MapNoteSheetEditable, MapNoteSheet, MapNoteSheetNew } from '../../components';
import MapView from './MapView';
import PoiListContainer from './PoiListContainer';
import PoiSheetContainer from './PoiSheetContainer';
import PublishContainer from './PublishContainer';
import ImportExportContainer from './ImportExportContainer';

const { Routing } = VmsState;

class Venue extends Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        alertModalOpen: PropTypes.bool.isRequired,
        failedSaves: PropTypes.array.isRequired,
        isSaving: PropTypes.bool.isRequired,
        isV5: PropTypes.bool.isRequired,
        loading: PropTypes.bool.isRequired,
        location: PropTypes.object.isRequired,
        poiEdits: PropTypes.object.isRequired,
        poiId: PropTypes.string,
        poiList: PropTypes.array.isRequired,
        venueBadgeUrl: PropTypes.string,
        venueError: PropTypes.string,
        venueId: PropTypes.string.isRequired,
        view: PropTypes.string.isRequired,
        selectedMapNote: PropTypes.object,
        isTypeDropdownOpen: PropTypes.bool,
        updatedMapNoteFields: PropTypes.object.isRequired,
        mapNotelets: PropTypes.arrayOf(PropTypes.object).isRequired,
        mapNoteTotals: PropTypes.object.isRequired,
        exitMapNoteModalOpen: PropTypes.bool,
        trashMapNoteModalOpen: PropTypes.bool,
        cancelChangesModalOpen: PropTypes.bool,
        mapNoteComment: PropTypes.string,
        userEmail: PropTypes.string,
        mapNoteLoading: PropTypes.bool,
        mapNoteSheetVersion: PropTypes.string,
    };

    static defaultProps = {
        poiId: null,
        venueBadgeUrl: '',
        venueError: null,
        isTypeDropdownOpen: false,
        exitMapNoteModalOpen: false,
        trashMapNoteModalOpen: false,
        cancelChangesModalOpen: false,
        mapNoteComment: '',
        selectedMapNote: null,
        userEmail: 'test@test.com',
        mapNoteLoading: false,
        mapNoteSheetVersion: '',
    };

    componentDidUpdate = () => this.props.dispatch(VmsState.MultiThunks.selectVenue(this.props.venueId));

    // =================================================================================================================

    returnToViewMapNoteSheet = () => {
        this.props.dispatch(VmsState.MapNotesReducer.Actions.clearUpdatedFields());
        this.props.dispatch(VmsState.MapNotesReducer.Actions.setSheetVersion('view'));
    };

    onMapNoteCommentChange = comment => this.props.dispatch(VmsState.MapNotesReducer.Actions.setComment(comment));

    onMapNoteCommentUpload = () => {
        const { dispatch, venueId, userEmail } = this.props;
        dispatch(VmsState.MapNotesReducer.Thunks.addMapNoteCommentText(venueId, userEmail));
    };

    onMapNoteImageUpload = images => {
        const { dispatch, venueId, userEmail } = this.props;
        dispatch(VmsState.MapNotesReducer.Thunks.addMapNoteCommentImages(images, venueId, userEmail));
    };

    onMapNoteCommentDelete = commentId => {
        const { dispatch, venueId, userEmail } = this.props;
        dispatch(VmsState.MapNotesReducer.Thunks.deleteMapNoteComment(commentId, venueId, userEmail));
    };

    onMapNoteImageDelete = image => {
        const updatedImagesList = R.without([image], this.props.updatedMapNoteFields.uploadedImages);
        this.onMapNoteUpdate('uploadedImages', updatedImagesList);
    };

    onMapNoteStatusChange = () => {
        const { dispatch, selectedMapNote, venueId } = this.props;
        const newNoteStatus = selectedMapNote.noteStatus === 'open' ? 'resolved' : 'open';
        dispatch(VmsState.MapNotesReducer.Actions.setUpdatedFields({ noteStatus: newNoteStatus }));
        dispatch(VmsState.MapNotesReducer.Thunks.updateMapNote(venueId));
    };

    onMapNoteSheetClose = () => {
        R.isEmpty(this.props.updatedMapNoteFields) && R.isEmpty(this.props.mapNoteComment)
            ? this.props.dispatch(VmsState.MultiThunks.onMapNoteSheetClose())
            : this.props.dispatch(VmsState.MapNotesReducer.Actions.setShowExitMapNoteModal(true));
    };

    onMapNoteCancelEditChanges = () => {
        R.isEmpty(this.props.updatedMapNoteFields) ? this.returnToViewMapNoteSheet() : this.props.dispatch(VmsState.MapNotesReducer.Actions.setShowCancelChangesModal(true));
    };

    onMapNoteNewImageUpload = images => {
        const oldImagesList = R.propOr([], 'uploadedImages', this.props.updatedMapNoteFields);
        const updatedImagesList = R.concat(oldImagesList, images);
        this.onMapNoteUpdate('uploadedImages', updatedImagesList);
    };

    onMapNoteUpdate = (key, value) => {
        const { dispatch } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.setUpdatedFields({ [key]: MapNote.MapNote.isNoType(value) }));
    };

    onTrashMapNote = async isTrashed => {
        const { dispatch, venueId, userEmail } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.setShowTrashMapNoteModal(false));
        if (isTrashed) {
            await dispatch(VmsState.MapNotesReducer.Thunks.deleteMapNote(venueId, userEmail));
            await dispatch(VmsState.MultiThunks.onMapNoteSheetClose());
        }
    };

    onCloseMapNote = isExited => {
        this.props.dispatch(VmsState.MapNotesReducer.Actions.setShowExitMapNoteModal(false));
        if (isExited) {
            this.props.dispatch(VmsState.MultiThunks.onMapNoteSheetClose());
        }
    };

    onCancelMapNoteChanges = isCancelled => {
        const { dispatch, mapNoteSheetVersion } = this.props;
        this.props.dispatch(VmsState.MapNotesReducer.Actions.setShowCancelChangesModal(false));
        if (isCancelled) {
            mapNoteSheetVersion === 'edit' ? this.returnToViewMapNoteSheet() : dispatch(VmsState.MultiThunks.onMapNoteSheetClose());
        }
    };

    saveMapNoteChanges = async () => {
        const { dispatch, venueId } = this.props;
        await dispatch(VmsState.MapNotesReducer.Thunks.updateMapNote(venueId));
        await dispatch(VmsState.MapNotesReducer.Actions.setSheetVersion('view'));
    };

    renderMapNoteSheet = () => (
        <MapNoteSheet
            loading={this.props.mapNoteLoading}
            mapNote={this.props.selectedMapNote}
            comment={this.props.mapNoteComment}
            userEmail={this.props.userEmail}
            onCommentUpload={this.onMapNoteCommentUpload}
            onCommentChange={this.onMapNoteCommentChange}
            onImageUpload={this.onMapNoteImageUpload}
            onCommentDelete={this.onMapNoteCommentDelete}
            onStatusChange={this.onMapNoteStatusChange}
            onClose={this.onMapNoteSheetClose}
            onEdit={() => this.props.dispatch(VmsState.MapNotesReducer.Actions.setSheetVersion('edit'))}
            onTrash={() => this.props.dispatch(VmsState.MapNotesReducer.Actions.setShowTrashMapNoteModal(true))}
        />
    );

    renderMapNoteSheetEditable = () => (
        <MapNoteSheetEditable
            loading={this.props.mapNoteLoading}
            userEmail={this.props.userEmail}
            mapNote={this.props.selectedMapNote}
            updatedFields={this.props.updatedMapNoteFields}
            onTrash={() => this.props.dispatch(VmsState.MapNotesReducer.Actions.setShowTrashMapNoteModal(true))}
            onClose={this.onMapNoteSheetClose}
            onCancel={this.onMapNoteCancelEditChanges}
            onSubjectChange={updatedText => this.onMapNoteUpdate('subject', updatedText)}
            onTypeChange={updatedType => this.onMapNoteUpdate('noteType', updatedType)}
            onSave={this.saveMapNoteChanges}
        />
    );

    renderMapNoteSheetNew = () => (
        <MapNoteSheetNew
            loading={this.props.mapNoteLoading}
            updatedFields={this.props.updatedMapNoteFields}
            onSubjectChange={updatedText => this.onMapNoteUpdate('subject', updatedText)}
            onDescriptionChange={updatedText => this.onMapNoteUpdate('description', updatedText)}
            onTypeChange={updatedType => this.onMapNoteUpdate('noteType', updatedType)}
            onImageUpload={this.onMapNoteNewImageUpload}
            onImageDelete={this.onMapNoteImageDelete}
            onClose={this.onMapNoteSheetClose}
            onSave={() => this.props.dispatch(VmsState.MultiThunks.createMapNote())}
        />
    );

    renderAMapNoteSheetVersion = () => {
        const version = () => {
            switch (this.props.mapNoteSheetVersion) {
                case 'view':
                    return this.renderMapNoteSheet();
                case 'edit':
                    return this.renderMapNoteSheetEditable();
                case 'new':
                    return this.renderMapNoteSheetNew();
                case 'loading':
                    return <SkeletonLoader />;
                default:
                    return <div />;
            }
        };
        return version();
    };

    // =================================================================================================================

    closeAlertModal = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.ViewReducer.Actions.hideVenueChangeAlert());
    };

    renderPoiSheet = () => (
        <div className={s.poiSheetContainer}>
            <PoiSheetContainer className={s.poiSheet} />
        </div>
    );

    onSelectFailedPoi = id => {
        const { venueId, dispatch } = this.props;
        if (id) {
            dispatch(Routing.Actions.goToPoi(venueId, id));
        }
    };

    /*
     * Happens after an upload failure if the user has decided to throw away all their changes.
     * We *should* confirm that the user *really* wants to throw them away, but we're not really handling this yet
     * The text of the confirmation *would* be one of:
     *
     *   `You will lose the changes made to this POI'
     *   `You will lose all of the changes to these POI's;
     */
    onCancelAllUpdates = () => {
        // const onCloseConfirmation = shouldRevertFailures => {
        //     if (shouldRevertFailures) this.props.onRevertFailures()
        // }

        throw new Error('onCancelAllUpdates not implemented yet');
    };

    onRetry = () => {
        const { poiEdits, venueId, dispatch, poiList } = this.props;
        R.forEachObjIndexed((changes, uuid) => {
            if (R.endsWith('_NEW', uuid)) {
                dispatch(VmsState.MultiThunks.createNewPoi(venueId, changes));
            } else {
                let entity;
                if (changes) {
                    entity = R.find(R.propEq(uuid, 'uuid'), poiList);
                    entity = R.mergeRight(entity, changes);
                }
                dispatch(VmsState.MultiThunks.updateEntities(venueId, [uuid], changes, false, null, entity));
            }
        }, poiEdits);
    };

    onRevert = () => {
        this.props.dispatch(VmsState.PoiStatusReducers.Actions.revertAllEdits());
    };

    onViewSelect = view => {
        const { location, dispatch, venueId } = this.props;
        // stay on poi or venue for map or list, but force venue otherwise

        if (R.includes(view, ['map', 'list'])) {
            dispatch(Routing.Actions.changeView(location, view));
        } else {
            dispatch(Routing.Actions.goToVenue(venueId, { view }));
        }
    };

    onChangeVenue = venueId => {
        const { dispatch } = this.props;
        dispatch(Routing.Actions.goToVenue(venueId));
    };

    render = () => {
        const { view, venueId, poiId, poiList, loading, isV5, isSaving, failedSaves, alertModalOpen, venueBadgeUrl, exitMapNoteModalOpen, trashMapNoteModalOpen, cancelChangesModalOpen } = this.props;

        const venueList = LandscapeState.selectors.getVenueSummaries();
        const mapView = view === 'map';
        const poiAreaClasses = classNames(s.poiArea, { [s.poiSheetShowing]: !!poiId }, s[`${view}ViewShowing`]);
        const showPoiSheet = !!poiId;

        const topBarProps = () => ({
            venueId,
            venueBadgeUrl,
            failedSaves,
            isSaving,
            isV5,
            venueSummaries: venueList,
            selectedView: view,
            onRetryFailures: this.onRetry,
            onRevertFailures: this.onRevert,
            onVenueSelect: this.onChangeVenue,
            onViewSelect: this.onViewSelect,
            onSelectFailedPoi: this.onSelectFailedPoi,
            onCancelAllUpdates: this.onCancelAllUpdates,
        });

        return (
            <div className={classNames(s.venue, `${view}Showing`)}>
                {view !== 'import' && <TopBar {...topBarProps()} />}
                <div className={poiAreaClasses}>
                    <div className={s.selectedViewArea}>
                        <PoiListContainer className={s.poiListView} loading={loading} poiList={poiList} />
                        <MapView className={s.mapView} loading={loading} poiSheetShowing={showPoiSheet} style={{ display: mapView ? 'block' : 'none' }} venueId={venueId} />
                        <div className={s.publishView}>
                            <PublishContainer />
                        </div>
                        <div className={s.importView}>
                            <ImportExportContainer />
                        </div>
                    </div>
                    {showPoiSheet && this.renderPoiSheet()}
                    {this.renderAMapNoteSheetVersion()}
                </div>
                <ConfirmationModal
                    open={exitMapNoteModalOpen}
                    title="Closing the note means your changes will not be saved."
                    subtitle="Are you sure you want to continue?"
                    okLabel="Close"
                    onClose={this.onCloseMapNote}
                />
                <ConfirmationModal open={trashMapNoteModalOpen} title="Are you sure you want to delete this note?" subtitle="" okLabel="Delete Note" onClose={this.onTrashMapNote} />
                <ConfirmationModal
                    open={cancelChangesModalOpen}
                    title="Cancelling your changes means that they will not be saved."
                    subtitle="Are you sure you want to continue?"
                    onClose={this.onCancelMapNoteChanges}
                />
            </div>
        );
    };
}

const mapStateToProps = state => {
    const poiId = Routing.Selectors.getLocationPoiId(state);
    const venueId = Routing.Selectors.getLocationVenueId(state);
    const isV5 = LandscapeState.selectors.isVenueV5(venueId, state);
    const view = Routing.Selectors.getLocationView(state);
    const poiSearchText = VmsState.ViewReducer.Selectors.getPoiSearchText(state);
    const isSaving = VmsState.PoiStatusReducers.Selectors.isSaving(state);
    const alertModalOpen = VmsState.ViewReducer.Selectors.getShowVenueChangeAlert(state);
    const poiList = LandscapeState.selectors.searchEntities(venueId, poiSearchText);
    const failedSaves = VmsState.PoiStatusReducers.Selectors.getFailedPoiSaves(state);
    const poiEdits = VmsState.PoiStatusReducers.Selectors.getPoiEdits(state);
    const venueCategory = LandscapeState.selectors.getVenueCategory(venueId, state);
    const userData = VmsState.SessionReducer.Selectors.getUserData(state);
    const accountId = VmsState.SessionReducer.Selectors.getAccountId(state);
    const venueBadgeUrl = CanonicalLoader.urlForBadge(venueCategory, venueCategory, venueId, accountId);
    const loading = LandscapeState.selectors.isLoadingVenue(state);
    const venueError = LandscapeState.selectors.getVenueLoadingError(state);

    // Map Notes
    const userEmail = userData ? userData.email : '';
    const selectedMapNote = VmsState.MapNotesReducer.Selectors.getSelectedMapNote(state);
    const isTypeDropdownOpen = VmsState.MapNotesReducer.Selectors.isTypeDropdownOpen(state);
    const updatedMapNoteFields = VmsState.MapNotesReducer.Selectors.getUpdatedFields(state);
    const mapNotelets = VmsState.MapNotesReducer.Selectors.getMapNotelets(state);
    const mapNoteTotals = VmsState.MapNotesReducer.Selectors.getTotals(state);
    const exitMapNoteModalOpen = VmsState.MapNotesReducer.Selectors.getShowExitMapNoteModal(state);
    const trashMapNoteModalOpen = VmsState.MapNotesReducer.Selectors.getShowTrashMapNoteModal(state);
    const cancelChangesModalOpen = VmsState.MapNotesReducer.Selectors.getShowCancelChangesModal(state);
    const mapNoteComment = VmsState.MapNotesReducer.Selectors.getComment(state);
    const mapNoteSheetVersion = VmsState.MapNotesReducer.Selectors.getSheetVersion(state);
    const mapNoteLoading = VmsState.MapNotesReducer.Selectors.getLoading(state);

    // DEBUG ONLY- force error to appear
    // if (failedSaves.length === 0) {
    //   failedSaves.push({ id: 'testjax_2', name: 'Boons fake saved Poi attempt' })
    // }

    return {
        alertModalOpen,
        failedSaves,
        isSaving,
        loading,
        location: state.location,
        poiEdits,
        poiId,
        poiList,
        venueBadgeUrl,
        venueError,
        venueId,
        view,
        selectedMapNote,
        isTypeDropdownOpen,
        updatedMapNoteFields,
        mapNotelets,
        mapNoteTotals,
        exitMapNoteModalOpen,
        trashMapNoteModalOpen,
        cancelChangesModalOpen,
        mapNoteComment,
        isV5,
        userEmail,
        mapNoteSheetVersion,
        mapNoteLoading,
    };
};

export default connect(mapStateToProps)(Venue);
